import { useState } from "react";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import { carrierOptions } from "../../../../constants";
import { ANALYTICS_EVENTS, trackEvent } from "../../../../lib/analytics";

interface TrackingEditFormProps {
  defaultTrackingNumber: string;
  defaultCarrier: string;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (trackingInfo: { trackingNumber: string; carrier: string }) => void;
}

export default function TrackingEditForm({
  defaultTrackingNumber,
  defaultCarrier,
  isSaving,
  onCancel,
  onSave,
}: TrackingEditFormProps) {
  const [trackingNumber, setTrackingNumber] = useState(defaultTrackingNumber);
  const [carrier, setCarrier] = useState(defaultCarrier);

  const handleSave = () => {
    const trimmedTrackingNumber = trackingNumber.trim();
    if (!trimmedTrackingNumber) return;

    trackEvent(ANALYTICS_EVENTS.TRACKING_STATUS_UPDATE_CLICKED);
    onSave({ trackingNumber: trimmedTrackingNumber, carrier });
  };

  return (
    <div className="flex flex-col gap-4 mt-2 w-full animate-in fade-in">
      <div className="flex flex-col gap-1">
        <label className="text-slate-400 md:text-xl text-xs tracking-wider font-normal">
          Tracking ID
        </label>
        <input
          type="text"
          autoFocus
          value={trackingNumber}
          onChange={(e) => setTrackingNumber(e.target.value)}
          className="w-full font-normal text-slate-600 md:text-lg text-sm border border-blue-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-slate-400 md:text-xl text-xs tracking-wider font-normal">
          Carrier
        </label>
        <Listbox value={carrier} onChange={setCarrier}>
          <div className="relative">
            <ListboxButton className="flex w-full items-center justify-between border border-blue-200 rounded-lg px-3 py-2 bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500">
              <span className="font-normal text-slate-600 md:text-lg text-sm truncate">
                {carrier}
              </span>
              <ChevronDownIcon className="h-4 w-4 text-gray-500" />
            </ListboxButton>
            <ListboxOptions
              anchor="bottom"
              className="mt-1 w-(--button-width) rounded-xl border border-gray-200 bg-white p-1 shadow-lg outline-none z-20"
            >
              {carrierOptions.map((option) => (
                <ListboxOption
                  key={option}
                  value={option}
                  className="cursor-pointer select-none rounded-lg px-3 py-2 text-sm data-focus:bg-blue-50 data-focus:text-blue-500"
                >
                  {option}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>
      </div>

      <div className="flex gap-2 w-full mt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 bg-white border border-slate-200 text-slate-700 py-2.5 rounded-lg font-medium text-sm hover:bg-slate-50 transition-colors shadow-sm cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors shadow-sm cursor-pointer"
        >
          {isSaving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}
